import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useTablet } from '@/composables/useTablet';

/**
 * AB#22137 phase 5 — the dashboard's list and order on a tablet (768-1024px).
 *
 * Half of a tablet is too narrow for either the list or the order, so an open
 * order takes the whole width, as the expanded view does on a desktop. The
 * order strip's control goes back to the list, full width, with the open
 * order tabs waiting behind it; opening an order (a row, a tab, a card)
 * brings the order back.
 *
 * Above 1024px `listGivesWay` is exactly `paneExpanded` and the toggle is the
 * desktop expand, so the split works as before.
 */
export const useTabletSplit = (opts: {
  paneExpanded: Readonly<Ref<boolean>>;
  togglePaneExpanded: (value?: boolean) => void;
}): {
  isTablet: Readonly<Ref<boolean>>;
  /** On a tablet with an order open: the list is showing instead of it. */
  tabletList: Ref<boolean>;
  /** The list steps aside for the order. */
  listGivesWay: ComputedRef<boolean>;
  onToggleExpand: () => void;
  /** An order was opened: show it. */
  showOrder: () => void;
  /** Esc: on a tablet, back to the list. True when it handled the key. */
  onEscape: (orderOpen: boolean) => boolean;
} => {
  const isTablet = useTablet();
  const tabletList = ref(false);
  const listGivesWay = computed(
    () => opts.paneExpanded.value || (isTablet.value && !tabletList.value)
  );
  const onToggleExpand = (): void => {
    if (isTablet.value) tabletList.value = !tabletList.value;
    else opts.togglePaneExpanded();
  };
  const showOrder = (): void => {
    tabletList.value = false;
  };
  const onEscape = (orderOpen: boolean): boolean => {
    if (!isTablet.value || !orderOpen || tabletList.value) return false;
    tabletList.value = true;
    return true;
  };
  watch(isTablet, (on) => {
    if (!on) tabletList.value = false;
  });
  return { isTablet, tabletList, listGivesWay, onToggleExpand, showOrder, onEscape };
};
